import React, { useReducer, useRef, useEffect, useState, useContext } from 'react';
import { styled, Caption, CustomLink, LazyImage, Body } from '@teko/ds-desktop';
import fnsFormat from 'date-fns/format';
import { EnhancedImage, ProductPrice } from '@teko/teko-ecommerce';
import { Tag } from '@teko/ds-foundation';
import { __rest } from 'tslib';
import { usePopper } from 'react-popper';
import i18next from 'i18next';
import { EditOutlined } from '@teko/ds-icon';

const initialState = {
    referenceElement: null,
    toggle: () => { },
    isOpen: false,
    toggleType: '',
};
const DropdownContext = React.createContext(initialState);
const DropdownProvider = ({ children, value }) => {
    const [state, dispatch] = useReducer((state = initialState, action) => {
        switch (action.type) {
            case 'SET_REFERENCE':
                return Object.assign(Object.assign({}, state), { referenceElement: action.payload });
            default:
                return state;
        }
    }, initialState);
    return (React.createElement(DropdownContext.Provider, { value: { state: Object.assign(Object.assign({}, state), value), dispatch } }, children));
};

const Dropdown = (_a) => {
    var { children, isOpen, toggle, toggleType = 'click', className, onHide } = _a, props = __rest(_a, ["children", "isOpen", "toggle", "toggleType", "className", "onHide"]);
    const wrapperRef = useRef(null);
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                toggle();
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [wrapperRef, isOpen]);
    return (React.createElement(DropdownProvider, { value: { isOpen, toggle, toggleType } },
        React.createElement(DropdownWrapper, Object.assign({ onMouseEnter: toggleType === 'hover' ? toggle : null, onMouseLeave: toggleType === 'hover' ? onHide : null, className: className, ref: wrapperRef }, props), children)));
};
const DropdownWrapper = styled.div `
  width: 100%;
`;

const DropdownToggle = ({ children, className, }) => {
    const [anchor, setAnchor] = useState(null);
    const { state: { toggle, toggleType }, dispatch, } = useContext(DropdownContext);
    useEffect(() => {
        dispatch({ type: 'SET_REFERENCE', payload: anchor });
    }, [anchor]);
    return (React.createElement(StyledDiv$1, { className: className, ref: setAnchor, onClick: toggleType === 'click' ? toggle : null, onKeyUp: toggleType === 'click' ? toggle : null, tabIndex: 0 }, children));
};
const StyledDiv$1 = styled.div `
  width: 100%;
  &:focus {
    outline: none;
  }
`;

const Container = styled.div `
  [data-popper-reference-hidden] {
    visibility: hidden;
    pointer-events: none;
  }
`;
const Popper = (_a) => {
    var { children, referenceElement, className, placement } = _a, _b = _a.popperConfig, _c = _b === void 0 ? {} : _b, { modifiers = [] } = _c, rest = __rest(_c, ["modifiers"]);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, Object.assign({ placement, modifiers: [
            {
                name: 'arrow',
                options: { element: arrowElement },
            },
            {
                name: 'flip',
                enabled: true,
            },
            { name: 'hide' },
            ...modifiers,
        ] }, rest));
    useEffect(() => {
        if (update) {
            update();
        }
    }, [className]);
    return (React.createElement(Container, Object.assign({ ref: setPopperElement, style: styles.popper }, attributes.popper, { className: className }),
        children,
        React.createElement("div", { ref: setArrowElement, style: styles.arrow })));
};

const StyledDiv = styled.div(({ show }) => `
    background-color: transparent;
    opacity: ${show ? 1 : 0};
  `);
const StyledPopper = styled(Popper)(({ show }) => `
    visibility: ${show ? 'visible' : 'hidden'};
    z-index: 999;
  `);
const DropdownMenu = ({ children, popperConfig, placement = 'bottom-start', origin = 'center top', className, }) => {
    const { state: { isOpen, referenceElement }, } = useContext(DropdownContext);
    return (React.createElement(StyledPopper, { className: className, referenceElement: referenceElement, popperConfig: popperConfig, placement: placement, show: isOpen },
        React.createElement(StyledDiv, { show: isOpen, origin: origin }, children)));
};

const Wrapper$3 = styled.div `
  color: #848788;
  font-size: 0.875rem;
  display: flex;
  flex-wrap: wrap;
`;
const Variant = styled.div `
  display: flex;
`;
const Variants = ({ variants = [], }) => (React.createElement(Wrapper$3, null, variants.map((variant, index) => (React.createElement(Variant, { key: index },
    React.createElement(Caption, { level: 1, color: "textSecondary" },
        variant.name,
        ": ",
        variant.value),
    index !== variants.length - 1 && (React.createElement(Caption, { margin: ['s0', 's8'], color: "#E4E5F0" }, "|")))))));

var viLang = {
	"Được phép nâng giá bán tối đa": "Được phép nâng giá bán tối đa",
	"Giá muốn bán không được vượt quá {{var0}}": "Giá muốn bán không được vượt quá {{var0}}",
	"Giá muốn bán không được nhỏ hơn giá sản phẩm": "Giá muốn bán không được nhỏ hơn giá sản phẩm",
	"Giá sản phẩm": "Giá sản phẩm",
	"Giá muốn bán": "Giá muốn bán",
	"Nhập số tiền": "Nhập số tiền",
	"Số tiền chênh:": "Số tiền chênh:",
	"Sửa giá": "Sửa giá",
	"Dùng sau": "Dùng sau",
	"Áp dụng": "Áp dụng",
	"Tặng 1 Ba lô Acer": "Tặng 1 Ba lô Acer",
	"Tặng 1 bàn phím Logitech Tặng 1 bàn phím Logitech": "Tặng 1 bàn phím Logitech Tặng 1 bàn phím Logitech",
	"Bạn chỉ có thể mua tối đa {{maximumValue}} sản phẩm": "Bạn chỉ có thể mua tối đa {{maximumValue}} sản phẩm",
	"Chú ý": "Chú ý",
	"Bạn muốn xoá sản phẩm này ra khỏi giỏ hàng?": "Bạn muốn xoá sản phẩm này ra khỏi giỏ hàng?",
	"Đồng ý": "Đồng ý",
	"Hủy bỏ": "Hủy bỏ",
	"Xóa": "Xóa",
	"x{{ProductVoucherQuantity}} Voucher dùng cho đơn hàng sau": "x{{ProductVoucherQuantity}} Voucher dùng cho đơn hàng sau",
	"Phong vũ": "Phong vũ",
	"Không bán chịu": "Không bán chịu",
	"{{PrimaryName}} và {{var0}} sản phẩm khác": "{{PrimaryName}} và {{var0}} sản phẩm khác",
	"Chỉ áp dụng trên một số sản phẩm": "Chỉ áp dụng trên một số sản phẩm",
	"Giảm ": "Giảm ",
	"tối đa {{maxAmount}}": "tối đa {{maxAmount}}",
	"Dành": "Dành",
	"cho đơn hàng từ {{min}} đến {{max}}": "cho đơn hàng từ {{min}} đến {{max}}",
	"cho đơn hàng tối thiểu {{min}}": "cho đơn hàng tối thiểu {{min}}",
	"cho đơn hàng dưới {{max}}": "cho đơn hàng dưới {{max}}",
	"cho tất cả các đơn hàng": "cho tất cả các đơn hàng"
};

var enLang = {
	"Được phép nâng giá bán tối đa": "Max raised price allowed",
	"Giá muốn bán không được vượt quá {{var0}}": "Selling price cannot exceed {{var0}}",
	"Giá muốn bán không được nhỏ hơn giá sản phẩm": "Selling price cannot be less than supplier price",
	"Giá sản phẩm": "Supplier price",
	"Giá muốn bán": "Selling price",
	"Nhập số tiền": "Input amount",
	"Số tiền chênh:": "Gap amount:",
	"Sửa giá": "Edit price",
	"Dùng sau": "Use later",
	"Áp dụng": "Apply",
	"Tặng 1 Ba lô Acer": "Receive 1 Acer backpack",
	"Bạn chỉ có thể mua tối đa {{maximumValue}} sản phẩm": "You can only purchase max {{maximumValue}} products",
	"Chú ý": "Note",
	"Bạn muốn xoá sản phẩm này ra khỏi giỏ hàng?": "Are your sure to delete product from Cart?",
	"Đồng ý": "Agree",
	"Hủy bỏ": "Cancel",
	"Xóa": "Delete",
	"x{{ProductVoucherQuantity}} Voucher dùng cho đơn hàng sau": "x{{ProductVoucherQuantity}} Voucher used for next order",
	"Phong vũ": "Phong vũ",
	"Không bán chịu": "Not selling on credit",
	"{{PrimaryName}} và {{var0}} sản phẩm khác": "{{PrimaryName}} and {{var0}} other products",
	"Chỉ áp dụng trên một số sản phẩm": "Applicable for some specific products only",
	"Giảm ": "Discount ",
	"tối đa {{maxAmount}}": "max {{maxAmount}}",
	"Dành": "Applicable",
	"cho đơn hàng từ {{min}} đến {{max}}": "for the order from {{min}} to {{max}}",
	"cho đơn hàng tối thiểu {{min}}": "for the order from {{min}}",
	"cho đơn hàng dưới {{max}}": "for the order under {{max}}",
	"cho tất cả các đơn hàng": "for all orders"
};

const NAMESPACE = 'ui-cart';
const t = (keys, options) => {
    const text = i18next.t(keys, Object.assign(Object.assign({}, options), { ns: NAMESPACE, interpolation: { escapeValue: false } }));
    return text || keys;
};
const lang = {
    vi: viLang,
    en: enLang,
};

const formatPrice = (price) => {
    return price ? price.toLocaleString('vi-VN') : price;
};
const formatPriceFull = (price) => {
    return price || price === 0
        ? price.toLocaleString('vi-VN', {
            style: 'currency',
            currency: 'VND',
        })
        : price;
};
const sortItems = (childItems) => {
    return childItems.sort((left, right) => left.displayPriority > right.displayPriority ? 1 : -1);
};
const getComboName = (childItems) => {
    if (childItems.length === 0)
        return '';
    const primary = sortItems(childItems)[0];
    const comboLength = childItems.length;
    return comboLength === 1
        ? primary.name
        : t('{{PrimaryName}} và {{var0}} sản phẩm khác', {
            PrimaryName: primary.name,
            var0: comboLength - 1,
        });
};
const getComboImageUrl = (childItems) => {
    if (childItems.length === 0)
        return [];
    return sortItems(childItems).map((item) => { var _a, _b; return item.imageUrl || ((_b = (_a = item.product) === null || _a === void 0 ? void 0 : _a.productInfo) === null || _b === void 0 ? void 0 : _b.imageUrl); });
};
const getDateStringWithFormat = (dateString, format = 'HH:mm dd/MM/yyyy') => {
    try {
        return fnsFormat(new Date(dateString), format);
    }
    catch (_a) {
        return '';
    }
};
const getDateStringsTimeRange = (startDateString, endDateString) => {
    let str = '';
    const startDate = new Date(startDateString);
    const endDate = new Date(endDateString);
    if (startDate.getFullYear() !== endDate.getFullYear()) {
        str += `${getDateStringWithFormat(startDateString, 'dd/MM/yyyy')}`;
    }
    else {
        if (startDate.getMonth() !== endDate.getMonth()) {
            str += `${getDateStringWithFormat(startDateString, 'dd/MM')}`;
        }
        else {
            str += `${getDateStringWithFormat(startDateString, 'dd')}`;
        }
    }
    str += ` - ${getDateStringWithFormat(endDateString, 'dd/MM/yyyy')}`;
    return str;
};
const getBlockDescriptionForCartPromo = (promo, withTime = false) => {
    var _a, _b, _c;
    const condition = ((_a = promo === null || promo === void 0 ? void 0 : promo.basePromotion) === null || _a === void 0 ? void 0 : _a.condition) || (promo === null || promo === void 0 ? void 0 : promo.condition);
    if (!condition)
        return '';
    const { orderValueMin, orderValueMax } = condition;
    const minValue = formatPriceFull(orderValueMin);
    const maxValue = formatPriceFull(orderValueMax);
    const startedAt = (_b = promo === null || promo === void 0 ? void 0 : promo.basePromotion) === null || _b === void 0 ? void 0 : _b.startedAt;
    const endedAt = (_c = promo === null || promo === void 0 ? void 0 : promo.basePromotion) === null || _c === void 0 ? void 0 : _c.endedAt;
    let str = '';
    if (orderValueMin && orderValueMax) {
        str = `Áp dụng cho đơn hàng từ ${minValue} đến ${maxValue} `;
    }
    else if (orderValueMin) {
        str = `Áp dụng cho đơn hàng từ ${minValue} `;
    }
    else if (orderValueMax) {
        str = `Áp dụng cho đơn hàng tối đa ${maxValue} `;
    }
    else
        str = 'Áp dụng cho mọi đơn hàng';
    if (withTime) {
        str += ` . Áp dụng từ ${getDateStringsTimeRange(startedAt, endedAt)}`;
    }
    return str;
};
var PromoItemType;
(function (PromoItemType) {
    PromoItemType["GIFT"] = "gift";
    PromoItemType["DISCOUNT"] = "discount";
    PromoItemType["VOUCHER"] = "voucher";
})(PromoItemType || (PromoItemType = {}));
const getDiscountName = (discount) => {
    if (discount.flat) {
        return `Giảm ngay ${formatPriceFull(discount.flat)} vào giá trị đơn hàng`;
    }
    if (discount.percent) {
        const suffix = discount.maxAmountPerOrder
            ? ` (giảm tối đa trên đơn ${formatPriceFull(discount.maxAmountPerOrder)})`
            : '';
        return (`Giảm ngay ${discount.percent}% tối đa ${formatPriceFull(discount.maxAmount)}` + suffix);
    }
    return '';
};
const getVoucherName = (voucher) => {
    const defaultVoucherName = 'Tặng Voucher dùng cho đơn hàng sau';
    return !!voucher.name && !!voucher.quantity
        ? `${voucher.quantity} x ${voucher.name}`
        : defaultVoucherName;
};
const getGiftName = (gift) => {
    return `Quà tặng: ${gift.quantity} x ${gift.name} `;
};
const getBenefits = (benefit, withDiscount = true) => {
    const results = [];
    if (benefit) {
        if (benefit.discount && withDiscount) {
            results.push({
                type: PromoItemType.DISCOUNT,
                text: getDiscountName(benefit.discount),
            });
        }
        ((benefit === null || benefit === void 0 ? void 0 : benefit.gifts) || []).forEach((gift) => {
            const imgUrl = gift.imageUrl ? gift.imageUrl : '';
            results.push({
                type: PromoItemType.GIFT,
                text: getGiftName(gift),
                imgSrc: imgUrl,
            });
        });
        if (benefit.voucher) {
            results.push({
                type: PromoItemType.VOUCHER,
                text: getVoucherName(benefit.voucher),
            });
        }
    }
    return results;
};
const getPromotionProduct = (benefit) => {
    var _a, _b, _c, _d, _e, _f;
    const results = [];
    if (benefit === null || benefit === void 0 ? void 0 : benefit.discount) {
        if ((_a = benefit === null || benefit === void 0 ? void 0 : benefit.discount) === null || _a === void 0 ? void 0 : _a.percent) {
            results.push({
                type: PromoItemType.DISCOUNT,
                text: `Giảm ${(_b = benefit === null || benefit === void 0 ? void 0 : benefit.discount) === null || _b === void 0 ? void 0 : _b.percent}% tối đa ${formatPriceFull((_c = benefit === null || benefit === void 0 ? void 0 : benefit.discount) === null || _c === void 0 ? void 0 : _c.maxAmount)}`,
            });
        }
        else {
            results.push({
                type: PromoItemType.DISCOUNT,
                text: `Giảm tiền trực tiếp ${formatPriceFull((_d = benefit === null || benefit === void 0 ? void 0 : benefit.discount) === null || _d === void 0 ? void 0 : _d.flat)}`,
            });
        }
    }
    if (!!((_e = benefit === null || benefit === void 0 ? void 0 : benefit.gifts) === null || _e === void 0 ? void 0 : _e.length)) {
        (_f = benefit === null || benefit === void 0 ? void 0 : benefit.gifts) === null || _f === void 0 ? void 0 : _f.forEach((gift) => {
            results.push({
                type: PromoItemType.GIFT,
                text: `${(gift === null || gift === void 0 ? void 0 : gift.quantity) || 0}x ${(gift === null || gift === void 0 ? void 0 : gift.name) || ''}`,
            });
        });
    }
    if (!!(benefit === null || benefit === void 0 ? void 0 : benefit.voucher)) {
        results.push({
            type: PromoItemType.VOUCHER,
            text: `${benefit.voucher.quantity}x ${benefit.voucher.name} Mã giảm giá cho đơn hàng sau`,
        });
    }
    return results;
};
const getVoucherBenefit = (maxAmount, percent, flat, orderValueMin, orderValueMax, applyOn) => {
    let benefit = '';
    if (flat) {
        benefit += `${t('Giảm')} ${formatPriceFull(flat)}`;
    }
    else if (percent) {
        benefit += `${t('Giảm')} ${percent}%`;
        if (maxAmount) {
            benefit += ` ${t('tối đa {{maxAmount}}', {
                maxAmount: formatPriceFull(maxAmount),
            })}`;
        }
    }
    else {
        benefit += 'Dành';
    }
    if (orderValueMin && orderValueMax) {
        benefit += ` ${t('cho đơn hàng từ {{min}} đến {{max}}', {
            min: formatPriceFull(orderValueMin),
            max: formatPriceFull(orderValueMax),
        })}`;
    }
    else if (orderValueMin) {
        benefit += ` ${t('cho đơn hàng tối thiểu {{min}}', {
            min: formatPriceFull(orderValueMin),
        })}`;
    }
    else if (orderValueMax) {
        benefit += ` ${t('cho đơn hàng dưới {{max}}', {
            max: formatPriceFull(orderValueMax),
        })}`;
    }
    else {
        benefit += ` ${t('cho tất cả các đơn hàng')}`;
    }
    if (applyOn === 'part_order') {
        benefit += ` (${t('Chỉ áp dụng trên một số sản phẩm')})`;
    }
    return benefit;
};

const IMAGE_LIMIT = 4;
const ComboImage = ({ images, alt = [], keys = [], width, height, childHeight, childWidth, isContainBorder, }) => {
    const isSingleImage = images.length === 1;
    if (isSingleImage)
        return (React.createElement(Image$1, { src: images.shift(), height: height || 80, width: width || 80, alt: "product" }));
    const remainImage = images.length - IMAGE_LIMIT;
    return (React.createElement(ComboImageWrapper, { width: width, height: height },
        images.slice(0, IMAGE_LIMIT).map((image, index) => (React.createElement(ChildImage, { width: childWidth, height: childHeight, isContainBorder: isContainBorder, src: image, alt: alt[index], key: keys[index] || index }))),
        remainImage > 0 && React.createElement(RemainingNumber, null,
            "+",
            remainImage + 1)));
};
const ComboImageWrapper = styled.div((props) => ({
    width: props.width ? `${props.width}px` : '80px',
    height: props.height ? `${props.height}px` : '80px',
    background: '#F8F8FC',
    borderRadius: '4px',
    position: 'relative',
    cursor: 'pointer',
}));
const ChildImage = styled.img((props) => ({
    borderRadius: '4px',
    width: props.width ? `${props.width}px` : '34px',
    height: props.height ? `${props.height}px` : '34px',
    margin: '4px 0 0 4px',
    border: props.isContainBorder ? 'solid 1px #E4E5F0' : 'unset',
}));
const RemainingNumber = styled.div `
  position: absolute;
  width: 34px;
  height: 34px;
  border-radius: 4px;
  background: rgba(27, 29, 41, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  line-height: 16px;
  color: #ffffff;
  border: 4px;
  right: 4px;
  bottom: 4px;
`;
const Image$1 = styled(EnhancedImage) `
  border: 1px solid #e4e5f0;
`;

const Text = styled(Caption)((props) => `
flex: 1;
font-size: 0.75rem;
margin-top: auto;
margin-bottom: auto;
margin-left: 0.5rem;
text-decoration: ${props.disableProductPromo ? 'line-through' : 'none'};
`);
const Wrapper$2 = styled.div `
  display: flex;
  background: #fff;
  cursor: pointer;
`;
const ImageWrapper = styled.div `
  display: flex;
  align-items: center;
`;
const Gift = (_a) => {
    var { image, text, link, disableProductPromo, cartLineSelected = true, border = true } = _a, props = __rest(_a, ["image", "text", "link", "disableProductPromo", "cartLineSelected", "border"]);
    if (link)
        return (React.createElement(CustomLink, { href: link },
            React.createElement(Wrapper$2, Object.assign({}, props),
                React.createElement(ImageWrapper, null,
                    React.createElement(LazyImage, Object.assign({ contain: true, height: 32, width: 32, border: border }, image, { borderRadius: 2 }))),
                React.createElement(Caption, { level: 1 }, text),
                React.createElement(Text, { disableProductPromo: disableProductPromo }, text))));
    return (React.createElement(Wrapper$2, Object.assign({}, props),
        React.createElement(ImageWrapper, null,
            React.createElement(LazyImage, Object.assign({ contain: true, height: 32, width: 32, border: border }, image, { borderRadius: 2 }))),
        React.createElement(Text, { disableProductPromo: disableProductPromo && cartLineSelected }, text)));
};

const ProductInfo = ({ product: { sku, imageUrl, name, variants = [], combos = [], link, childItems = [], tags = [], }, className, }) => {
    const [comboOpen, setComboOpen] = useState(false);
    const isCombo = childItems.length > 0;
    const toggleCombo = () => {
        setComboOpen((prev) => !prev);
    };
    const image = isCombo ? (React.createElement(ComboImage, { images: getComboImageUrl(childItems) })) : (React.createElement(Image, { height: 80, width: 80, src: imageUrl, alt: "product" }));
    const productName = isCombo ? getComboName(childItems) : name;
    return (React.createElement(Wrapper$1, { className: className },
        React.createElement(CustomLink, Object.assign({}, link), image),
        React.createElement(Right, null,
            React.createElement(CustomLink, Object.assign({}, link),
                React.createElement(Body, { hoverColor: "primary500", level: 1, color: "textPrimary", weight: "regular", line: 2 },
                    tags && tags.length > 0 && (React.createElement(React.Fragment, null,
                        React.createElement(Tag, { type: "secondary", variant: "branding", content: tags[0] }),
                        "\u00A0")),
                    productName)),
            sku && (React.createElement(Caption, { level: 1, color: "textSecondary" },
                "SKU: ",
                sku)),
            !!variants.length && React.createElement(Variants, { variants: variants }),
            !!combos.length && (React.createElement(StyledDropdown, { isOpen: comboOpen, toggle: toggleCombo, toggleType: "hover" },
                React.createElement(DropdownToggle, { toggleType: "hover" },
                    React.createElement(Action, { mgl: false }, "Xem combo")),
                React.createElement(DropdownMenu, { popperConfig: popperConfig, origin: "left top" },
                    React.createElement(ComboList, null, combos.map(({ imageUrl, name }, index) => (React.createElement(StyledGift, { key: index, image: {
                            src: imageUrl,
                        }, text: name }))))))))));
};
const Wrapper$1 = styled.div `
  display: flex;
  gap: 12px;
`;
const Right = styled.div `
  flex: 1;
`;
const Action = styled.span(({ mgl = true }) => `
      font-size: 14px;
      color: #047fff;
      margin-left: ${mgl ? '1.5rem' : 0};
      cursor: pointer;
      display: inline-block;
      width: max-content;
      flex-shrink: 0;
    `);
const ComboList = styled.ul `
  padding: 0.25rem 0.75rem;
  list-style-type: none;
  border-radius: 0.5rem;
  box-shadow: 0 2px 0.5rem 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  background-color: white;
  margin: 0;
  width: max-content;
  max-width: 25rem;
`;
const StyledDropdown = styled(Dropdown) `
  width: max-content;
`;
const popperConfig = {
    modifiers: [
        {
            name: 'offset',
            options: {
                offset: [0, 10],
            },
        },
    ],
};
const StyledGift = styled(Gift) `
  margin: 0.5rem 0;
`;
const Image = styled(EnhancedImage) `
  border: 1px solid #e4e5f0;
`;

const ProductPriceCart = ({ supplierRetailPrice, latestPrice, isPriceEditable, onEditPrice, className, align, isMobile = true, }) => {
    return (React.createElement("div", null,
        React.createElement(Wrapper, { className: className },
            React.createElement(ProductPrice, { supplierRetailPrice: supplierRetailPrice, latestPrice: latestPrice, textColor: "textPrimary", align: align }),
            isPriceEditable && isMobile && (React.createElement(StyledEditIcon, { onClick: onEditPrice, size: "1.25rem" }))),
        isPriceEditable && !isMobile && (React.createElement(EditPriceWrapper, null,
            React.createElement(StyledButtonChangePrice, { level: 1, color: "link500", hoverColor: "error500", onClick: onEditPrice }, t('Sửa giá'))))));
};
const Wrapper = styled.div `
  display: flex;
`;
const StyledEditIcon = styled(EditOutlined) `
  margin-left: 4px;
`;
const StyledButtonChangePrice = styled(Body) `
  margin-top: 4px;
  cursor: pointer;
`;
const EditPriceWrapper = styled.div `
  justify-content: flex-end;
  display: flex;
  line-height: 20px;
`;

export { Gift as G, NAMESPACE as N, ProductInfo as P, ProductPriceCart as a, formatPriceFull as b, getComboImageUrl as c, getDateStringWithFormat as d, getDateStringsTimeRange as e, formatPrice as f, getComboName as g, getBlockDescriptionForCartPromo as h, PromoItemType as i, getBenefits as j, getPromotionProduct as k, lang as l, getVoucherBenefit as m, t };
